import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
    getPingLogDetail,
    PingLogListItem,
    PingLogRecord,
} from './pingLogService';
import './PingLog.css';

type AggregateSummary = {
    recordCount: number;
    totalRounds: number;
    avgLossPercent: number;
    avgRttMs: number;
    maxRttMs: number;
    deviceNames: string[];
    targetIps: string[];
    logIds: string[];
};

type AggregatedDetailPayload = {
    selectedItems: PingLogListItem[];
    aggregateSummary?: AggregateSummary;
    savedAt?: number;
};

type LocationState = {
    selectedItems?: PingLogListItem[];
    aggregateSummary?: AggregateSummary;
};

type MergedRoundRecord = PingLogRecord & {
    sourceLogId: string;
    sourceDeviceName: string;
    sourceTargetIp: string;
    sourceUploadTime: string;
};

const STORAGE_KEY = 'pinglog_aggregated_detail';

const PingLogAggregatedDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const locationState = (location.state || {}) as LocationState;

    const [payload, setPayload] = useState<AggregatedDetailPayload | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState('');
    const [mergedRecords, setMergedRecords] = useState<MergedRoundRecord[]>([]);
    const [isChartDragging, setIsChartDragging] = useState(false);

    useEffect(() => {
        const hasStateData =
            Array.isArray(locationState.selectedItems) &&
            locationState.selectedItems.length > 0;

        if (hasStateData) {
            const nextPayload: AggregatedDetailPayload = {
                selectedItems: locationState.selectedItems || [],
                aggregateSummary: locationState.aggregateSummary,
                savedAt: Date.now(),
            };

            setPayload(nextPayload);
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextPayload));
            return;
        }

        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (!cached) {
            setPayload(null);
            return;
        }

        try {
            const parsed = JSON.parse(cached) as AggregatedDetailPayload;

            if (parsed && Array.isArray(parsed.selectedItems) && parsed.selectedItems.length > 0) {
                setPayload(parsed);
            } else {
                setPayload(null);
            }
        } catch {
            setPayload(null);
        }
    }, [locationState.aggregateSummary, locationState.selectedItems]);

    useEffect(() => {
        const handleWindowMouseUp = () => {
            setIsChartDragging(false);
        };

        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, []);

    const selectedItems = payload?.selectedItems || [];

    useEffect(() => {
        const normalizeSortTime = (record: PingLogRecord) => {
            if (record.timestampUtc && record.timestampUtc.trim()) {
                return record.timestampUtc.trim();
            }

            const raw = (record.rawClockLine || '').trim();
            const dateMatch = raw.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
            const timeMatch = raw.match(/(\d{1,2}):(\d{2}):(\d{2})/);

            if (dateMatch && timeMatch) {
                const [, year, month, day] = dateMatch;
                const [, hour, minute, second] = timeMatch;
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute}:${second}`;
            }

            return '9999-12-31T23:59:59';
        };

        const loadMergedRecords = async () => {
            if (!selectedItems.length) {
                setMergedRecords([]);
                return;
            }

            try {
                setLoading(true);
                setLoadingError('');

                const details = await Promise.all(
                    selectedItems.map((item) => getPingLogDetail(item.logId))
                );

                const allMergedRecords: MergedRoundRecord[] = [];

                details.forEach((detail, idx) => {
                    const sourceItem = selectedItems[idx];

                    (detail?.records || []).forEach((record) => {
                        allMergedRecords.push({
                            ...record,
                            sourceLogId: sourceItem.logId || detail?.task?.logId || '',
                            sourceDeviceName:
                                record.deviceName ||
                                sourceItem.deviceName ||
                                detail?.task?.deviceName ||
                                '-',
                            sourceTargetIp:
                                record.targetIp ||
                                sourceItem.targetIp ||
                                detail?.task?.targetIp ||
                                '-',
                            sourceUploadTime: sourceItem.uploadTime || '',
                        });
                    });
                });

                allMergedRecords.sort((a, b) => {
                    return normalizeSortTime(a).localeCompare(normalizeSortTime(b));
                });

                setMergedRecords(allMergedRecords);
            } catch (err: any) {
                setLoadingError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'Failed to load aggregated round records.'
                );
                setMergedRecords([]);
            } finally {
                setLoading(false);
            }
        };

        loadMergedRecords();
    }, [selectedItems]);

    const formatValue = (value?: number | null) => {
        if (value === null || value === undefined) return '-';
        return value;
    };

    const formatClockLineForAxis = (raw?: string) => {
        if (!raw) return '-';

        const s = raw.trim();

        const dateMatch = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        const timeMatch = s.match(/(\d{1,2}):(\d{2}):(\d{2})/);

        if (!dateMatch || !timeMatch) {
            return s;
        }

        const [, year, month, day] = dateMatch;
        const [, hour, minute, second] = timeMatch;

        const mm = month.padStart(2, '0');
        const dd = day.padStart(2, '0');
        const hh = hour.padStart(2, '0');

        return `${year}${mm}${dd} ${hh}${minute}${second}`;
    };

    const formatClockLineShortForAxis = (raw?: string) => {
        if (!raw) return '-';

        const s = raw.trim();

        const dateMatch = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
        const timeMatch = s.match(/(\d{1,2}):(\d{2}):(\d{2})/);

        if (!timeMatch) {
            return s;
        }

        const [, hour, minute, second] = timeMatch;

        if (!dateMatch) {
            return `${hour.padStart(2, '0')}:${minute}:${second}`;
        }

        const [, , month, day] = dateMatch;

        return `${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour.padStart(2, '0')}:${minute}`;
    };

    const formatDateTime = (value?: string) => {
        if (!value) return '-';
        return value;
    };

    const taskSummary = useMemo(() => {
        if (!selectedItems.length) return null;

        const deviceNames = Array.from(
            new Set(selectedItems.map((item) => item.deviceName).filter((v) => !!v))
        );
        const targetIps = Array.from(
            new Set(selectedItems.map((item) => item.targetIp).filter((v) => !!v))
        );
        const logIds = selectedItems.map((item) => item.logId);

        return {
            selectedTasks: selectedItems.length,
            deviceNames,
            targetIps,
            logIds,
        };
    }, [selectedItems]);

    const roundSummary = useMemo(() => {
        if (!mergedRecords.length) {
            return {
                totalRounds: 0,
                avgLossPercent: 0,
                avgRttMs: 0,
                maxRttMs: 0,
                successRounds: 0,
                failedRounds: 0,
            };
        }

        const totalRounds = mergedRecords.length;

        const avgLossPercent =
            mergedRecords.reduce((sum, r) => sum + Number(r.lossPercent || 0), 0) / totalRounds;

        const rttAvgValues = mergedRecords
            .map((r) => Number(r.rttAvgMs))
            .filter((v) => Number.isFinite(v));

        const avgRttMs =
            rttAvgValues.length > 0
                ? rttAvgValues.reduce((sum, v) => sum + v, 0) / rttAvgValues.length
                : 0;

        const maxRttMs = Math.max(
            ...mergedRecords.map((r) => Number(r.rttMaxMs || 0))
        );

        const successRounds = mergedRecords.filter((r) => Number(r.lossPercent || 0) < 100).length;
        const failedRounds = mergedRecords.filter((r) => Number(r.lossPercent || 0) >= 100).length;

        return {
            totalRounds,
            avgLossPercent: Number(avgLossPercent.toFixed(2)),
            avgRttMs: Number(avgRttMs.toFixed(2)),
            maxRttMs,
            successRounds,
            failedRounds,
        };
    }, [mergedRecords]);

    const chartData = useMemo(() => {
        return mergedRecords.map((record, index) => {
            const rawLabel = record.rawClockLine || `Round ${index + 1}`;

            return {
                index: index + 1,
                clockLine: rawLabel,
                chartClockLine: formatClockLineForAxis(rawLabel),
                chartAxisShortLabel: formatClockLineShortForAxis(rawLabel),
                lossPercent: Number(record.lossPercent ?? 0),
                rttAvgMs: Number(record.rttAvgMs ?? 0),
                rttMinMs: Number(record.rttMinMs ?? 0),
                rttMaxMs: Number(record.rttMaxMs ?? 0),
                sourceLogId: record.sourceLogId,
                sourceDeviceName: record.sourceDeviceName,
                sourceTargetIp: record.sourceTargetIp,
            };
        });
    }, [mergedRecords]);

    const zoomStart = useMemo(() => {
        if (!chartData.length) return 0;
        if (chartData.length <= 50) return 0;
        return Math.max(0, ((chartData.length - 50) / chartData.length) * 100);
    }, [chartData]);

    const zoomEnd = 100;

    const chartOption = useMemo(() => {
        return {
            backgroundColor: 'transparent',
            animation: false,
            tooltip: {
                trigger: 'axis',
                confine: true,
                backgroundColor: '#ffffff',
                borderColor: '#dee2e6',
                borderWidth: 1,
                textStyle: {
                    color: '#212529',
                },
                formatter: (params: any) => {
                    if (!params || !params.length) return '';

                    const point = params[0]?.data;
                    if (!point) return '';

                    return `
                        <div style="min-width:320px;">
                            <div style="margin-bottom:8px;font-weight:600;">
                                Time: ${point.chartClockLine || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;word-break:break-word;">
                                Clock Line: ${point.clockLine || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Log ID: ${point.sourceLogId || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Device: ${point.sourceDeviceName || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#6c757d;">
                                Target IP: ${point.sourceTargetIp || '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#dc3545;">
                                PLR: ${point.lossPercent ?? '-'}
                            </div>
                            <div style="margin-bottom:6px;color:#0d6efd;">
                                RTD Avg: ${point.rttAvgMs ?? '-'}
                            </div>
                            <div style="color:#198754;">
                                RTD Max: ${point.rttMaxMs ?? '-'}
                            </div>
                        </div>
                    `;
                },
            },
            legend: {
                top: 8,
                textStyle: {
                    color: '#ffffff',
                },
            },
            grid: {
                top: 60,
                left: 50,
                right: 60,
                bottom: 135,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chartData.map((item) => item.chartAxisShortLabel),
                axisLabel: {
                    color: '#ffffff',
                    rotate: 45,
                    hideOverlap: true,
                    margin: 16,
                    fontSize: 11,
                    formatter: (value: string, index: number) => {
                        const step = chartData.length > 120 ? 8 : chartData.length > 60 ? 4 : 2;
                        return index % step === 0 ? value : '';
                    },
                },
                axisLine: {
                    lineStyle: {
                        color: 'rgba(255,255,255,0.25)',
                    },
                },
                axisTick: {
                    lineStyle: {
                        color: 'rgba(255,255,255,0.25)',
                    },
                },
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'PLR',
                    position: 'left',
                    axisLabel: {
                        color: '#ffffff',
                    },
                    nameTextStyle: {
                        color: '#ffffff',
                    },
                    splitLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                            type: 'dashed',
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                        },
                    },
                },
                {
                    type: 'value',
                    name: 'RTD Avg',
                    position: 'right',
                    axisLabel: {
                        color: '#ffffff',
                    },
                    nameTextStyle: {
                        color: '#ffffff',
                    },
                    splitLine: {
                        show: false,
                    },
                    axisLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.25)',
                        },
                    },
                },
            ],
            dataZoom: [
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    start: zoomStart,
                    end: zoomEnd,
                    zoomOnMouseWheel: false,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: true,
                    preventDefaultMouseMove: true,
                },
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    height: 24,
                    bottom: 30,
                    start: zoomStart,
                    end: zoomEnd,
                    borderColor: 'rgba(255,255,255,0.20)',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    fillerColor: 'rgba(13,110,253,0.28)',
                    dataBackground: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.35)',
                            width: 1,
                        },
                        areaStyle: {
                            color: 'rgba(255,255,255,0.10)',
                        },
                    },
                    selectedDataBackground: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.55)',
                            width: 1,
                        },
                        areaStyle: {
                            color: 'rgba(255,255,255,0.18)',
                        },
                    },
                    moveHandleSize: 10,
                    moveHandleStyle: {
                        color: 'rgba(13,110,253,0.85)',
                        borderColor: '#ffffff',
                        borderWidth: 1,
                        shadowBlur: 4,
                        shadowColor: 'rgba(0,0,0,0.25)',
                    },
                    handleSize: '95%',
                    handleStyle: {
                        color: 'rgba(13,110,253,0.95)',
                        borderColor: '#ffffff',
                        borderWidth: 1.2,
                        shadowBlur: 6,
                        shadowColor: 'rgba(0,0,0,0.28)',
                    },
                    textStyle: {
                        color: '#ffffff',
                    },
                    brushSelect: false,
                },
            ],
            series: [
                {
                    name: 'PLR',
                    type: 'line',
                    yAxisIndex: 0,
                    showSymbol: false,
                    smooth: false,
                    data: chartData.map((item) => ({
                        value: item.lossPercent,
                        ...item,
                    })),
                    lineStyle: {
                        width: 2,
                        color: '#dc3545',
                    },
                    itemStyle: {
                        color: '#dc3545',
                    },
                },
                {
                    name: 'RTD Avg',
                    type: 'line',
                    yAxisIndex: 1,
                    showSymbol: false,
                    smooth: false,
                    data: chartData.map((item) => ({
                        value: item.rttAvgMs,
                        ...item,
                    })),
                    lineStyle: {
                        width: 2,
                        color: '#0d6efd',
                    },
                    itemStyle: {
                        color: '#0d6efd',
                    },
                },
            ],
        };
    }, [chartData, zoomEnd, zoomStart]);

    const handleChartMouseDown = () => {
        setIsChartDragging(true);
    };

    const handleChartMouseUp = () => {
        setIsChartDragging(false);
    };

    const handleChartMouseLeave = () => {
        setIsChartDragging(false);
    };

    const handleBackToList = () => {
        navigate('/pinglog');
    };

    const handleClearCachedAggregatedData = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        navigate('/pinglog');
    };

    if (!taskSummary) {
        return (
            <div className="mt-3 pinglog-page">
                <Alert variant="warning">
                    No aggregated data found. Please go back and select records first.
                </Alert>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={handleBackToList}>
                        Back to List
                    </Button>
                    <Button variant="outline-danger" onClick={handleClearCachedAggregatedData}>
                        Clear Cached Data
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 pinglog-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">Aggregated Ping Log Detail</h4>
                    <div className="text-muted">
                        Round-level combined view for selected ping log tasks
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-danger" onClick={handleClearCachedAggregatedData}>
                        Clear Cached Data
                    </Button>
                    <Button variant="secondary" onClick={handleBackToList}>
                        Back to List
                    </Button>
                </div>
            </div>

            {loadingError && (
                <Alert variant="danger" className="mb-3">
                    {loadingError}
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-3">Selected Tasks Summary</h5>

                    <Row className="mb-2">
                        <Col md={3}>
                            <strong>Selected Tasks:</strong> {taskSummary.selectedTasks}
                        </Col>
                    </Row>

                    <Row className="mb-2">
                        <Col md={12}>
                            <strong>Devices:</strong>{' '}
                            {taskSummary.deviceNames.length ? taskSummary.deviceNames.join(', ') : '-'}
                        </Col>
                    </Row>

                    <Row className="mb-2">
                        <Col md={12}>
                            <strong>Target IPs:</strong>{' '}
                            {taskSummary.targetIps.length ? taskSummary.targetIps.join(', ') : '-'}
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <strong>Selected Log IDs:</strong>{' '}
                            {taskSummary.logIds.length ? taskSummary.logIds.join(', ') : '-'}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Row className="mb-3">
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Selected Tasks</div>
                            <h4 className="mb-0">{taskSummary.selectedTasks}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Total Rounds</div>
                            <h4 className="mb-0">{roundSummary.totalRounds}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Avg PLR</div>
                            <h4 className="mb-0">{roundSummary.avgLossPercent}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Avg RTD</div>
                            <h4 className="mb-0">{roundSummary.avgRttMs}</h4>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Max RTD</div>
                            <h4 className="mb-0">{roundSummary.maxRttMs}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Success Rounds</div>
                            <h4 className="mb-0">{roundSummary.successRounds}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Failed Rounds</div>
                            <h4 className="mb-0">{roundSummary.failedRounds}</h4>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <div className="text-muted">Load Status</div>
                            <h4 className="mb-0">{loading ? 'Loading...' : 'Ready'}</h4>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Round Trend</h5>
                        <div style={{ color: '#ffffff', fontSize: '0.875rem' }}>
                            Drag chart or bottom slider horizontally
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="text-center text-muted py-4">No round data</div>
                    ) : (
                        <div
                            style={{
                                width: '100%',
                                height: 460,
                                cursor: isChartDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                            }}
                            onMouseDown={handleChartMouseDown}
                            onMouseUp={handleChartMouseUp}
                            onMouseLeave={handleChartMouseLeave}
                        >
                            <ReactECharts
                                option={chartOption}
                                style={{
                                    height: '460px',
                                    width: '100%',
                                    cursor: isChartDragging ? 'grabbing' : 'grab',
                                }}
                                notMerge={true}
                                lazyUpdate={true}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-3">Selected Tasks</h5>

                    <Table striped bordered hover responsive className="pinglog-table">
                        <thead>
                        <tr>
                            <th>Log ID</th>
                            <th>Device</th>
                            <th>Target IP</th>
                            <th>Total Rounds</th>
                            <th>Avg PLR</th>
                            <th>Avg RTD</th>
                            <th>Max RTD</th>
                            <th>Upload Time</th>
                            <th>Upload User</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                            {selectedItems.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="text-center text-muted py-4">
                                        No selected tasks
                                    </td>
                                </tr>
                            ) : (
                                selectedItems.map((item) => (
                                    <tr key={item.logId}>
                                        <td style={{minWidth: '170px'}}>{item.logId}</td>
                                        <td>{item.deviceName || '-'}</td>
                                        <td>{item.targetIp || '-'}</td>
                                        <td>{item.totalRounds}</td>
                                        <td>{item.avgLossPercent}</td>
                                        <td>{item.avgRttMs ?? '-'}</td>
                                        <td>{item.maxRttMs ?? '-'}</td>
                                        <td style={{minWidth: '180px'}}>{formatDateTime(item.uploadTime)}</td>
                                        <td>{item.userName || '-'}</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                onClick={() => navigate(`/pinglog/${item.logId}`)}
                                            >
                                                View Raw Detail
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <h5 className="mb-3">Merged Round Records</h5>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Table striped bordered hover responsive className="pinglog-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Source Log ID</th>
                                    <th>Clock Line</th>
                                    <th>Device</th>
                                    <th>Target IP</th>
                                    <th>PLR</th>
                                    <th>RTD Min</th>
                                    <th>RTD Avg</th>
                                    <th>RTD Max</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mergedRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted py-4">
                                            No merged round records
                                        </td>
                                    </tr>
                                ) : (
                                    mergedRecords.map((record, index) => (
                                        <tr key={`${record.sourceLogId}-${record.timestampUtc || record.rawClockLine || index}`}>
                                            <td>{index + 1}</td>
                                            <td style={{ minWidth: '170px' }}>{record.sourceLogId || '-'}</td>
                                            <td style={{ minWidth: '220px' }}>{record.rawClockLine || '-'}</td>
                                            <td>{record.sourceDeviceName || '-'}</td>
                                            <td>{record.sourceTargetIp || '-'}</td>
                                            <td>{record.lossPercent ?? '-'}</td>
                                            <td>{formatValue(record.rttMinMs)}</td>
                                            <td>{formatValue(record.rttAvgMs)}</td>
                                            <td>{formatValue(record.rttMaxMs)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default PingLogAggregatedDetailPage;